import * as React from 'react';

import { cn } from '../../utils';
import { ChartContextProvider, type ChartContextValue } from './chart-context';
import { buildLinearScale, buildXScale, DEFAULT_MARGIN } from './scales';
import type { ChartMargin, XScaleKind } from './types';
import { useChartSize } from './use-chart-size';

export interface ChartRootProps {
  /** Ordered unique x-values present in the data */
  domain: (string | number)[];
  /** Values plotted against the left y-axis — drives its extent */
  values: number[];
  /** Values plotted against the right y-axis; enables the second scale */
  valuesRight?: number[];
  /** X-scale kind — categorical band (default), linear, or time */
  xScaleKind?: XScaleKind;
  height: number;
  margin?: Partial<ChartMargin>;
  /** Band padding of the x-scale (0–1) */
  bandPadding?: number;
  className?: string;
  /** Accessible chart description */
  ariaLabel?: string;
  /** Rendered outside the translated group, e.g. tooltips */
  overlay?: React.ReactNode;
  children: React.ReactNode;
}

/** Responsive SVG container providing scales and geometry to chart primitives. */
export const ChartRoot = ({
  domain,
  values,
  valuesRight,
  xScaleKind = 'band',
  height,
  margin,
  bandPadding = 0.2,
  className,
  ariaLabel,
  overlay,
  children
}: ChartRootProps) => {
  const { ref, width } = useChartSize();
  const resolvedMargin: ChartMargin = { ...DEFAULT_MARGIN, ...margin };
  // A right axis needs label room; keep the default tight otherwise.
  const hasRightAxis = valuesRight !== undefined && valuesRight.length > 0;
  const { left, top, bottom } = resolvedMargin;
  const right = hasRightAxis && margin?.right === undefined ? left : resolvedMargin.right;
  const innerWidth = Math.max(width - left - right, 0);
  const innerHeight = Math.max(height - top - bottom, 0);
  // Arrays are compared by content — callers may rebuild them every render.
  const domainKey = domain.join('\u0000');
  const valuesKey = values.join('\u0000');
  const valuesRightKey = valuesRight?.join('\u0000');

  const context = React.useMemo<ChartContextValue>(
    () => ({
      dimensions: {
        width,
        height,
        innerWidth,
        innerHeight,
        margin: { left, right, top, bottom }
      },
      xScale: buildXScale({ kind: xScaleKind, domain, width: innerWidth, bandPadding }),
      yScale: buildLinearScale(values, innerHeight),
      yScaleRight: valuesRight ? buildLinearScale(valuesRight, innerHeight) : undefined
    }),
    // The array props are tracked through their serialized keys, not by identity.
    [
      width,
      height,
      innerWidth,
      innerHeight,
      bandPadding,
      xScaleKind,
      domainKey,
      valuesKey,
      valuesRightKey,
      left,
      right,
      top,
      bottom
    ]
  );

  return (
    <div ref={ref} className={cn('relative w-full', className)} style={{ height }}>
      <svg
        width='100%'
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role='img'
        aria-label={ariaLabel}
        data-slot='chart-root'
      >
        <g transform={`translate(${left},${top})`}>
          <ChartContextProvider value={context}>{children}</ChartContextProvider>
        </g>
      </svg>
      {overlay && <ChartContextProvider value={context}>{overlay}</ChartContextProvider>}
    </div>
  );
};

ChartRoot.displayName = 'ChartRoot';
